import React, { useState } from 'react';
import { Panel, StatusBadge } from './AdminPrimitives';
import { DataTable, type Column } from './DataTable';
import { Button } from '../ui/Button';
import { communities } from '../../data/communities';
import { communityChallenges } from '../../data/engagement';
import { recordAudit } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
import type { ChallengeDuration, ChallengeMetric, CommunityChallenge } from '../../types/engagement';
import { challengeTimeline, suggestedEndDate } from '../../utils/challenges';
const durations: ChallengeDuration[] = ['weekly', 'monthly', 'annual'];
const metrics: ChallengeMetric[] = ['distanceKm', 'workouts', 'swimKm', 'mobilitySessions', 'yogaSessions', 'hydrationDays', 'checkDays'];
const metricLabel: Record<ChallengeMetric, string> = {
  distanceKm: 'Distância de corrida',
  workouts: 'Número de treinos',
  swimKm: 'Distância de natação',
  mobilitySessions: 'Sessões de mobilidade',
  yogaSessions: 'Sessões de yoga',
  hydrationDays: 'Dias de hidratação',
  checkDays: 'Dias de Daily Check'
};
const statusLabel = {
  upcoming: 'Agendado',
  active: 'Ativo',
  ended: 'Encerrado'
} as const;
const statusTone = {
  upcoming: 'scheduled',
  active: 'active',
  ended: 'inactive'
} as const;
function todayISO(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
function brDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * ADM/Gamification/Challenges — one challenge model for weekly, monthly and 12-month
 * challenges. Prototype only: created challenges live until reload; the backend owns
 * lifecycle, participation, progress and checkpoints.
 */
export function ChallengeAdmin() {
  const {
    t,
    tl
  } = useLocale();
  const [rows, setRows] = useState<CommunityChallenge[]>(communityChallenges);
  const [filter, setFilter] = useState<ChallengeDuration | 'all'>('all');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const start0 = todayISO();
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState<ChallengeDuration>('monthly');
  const [communityId, setCommunityId] = useState(communities[0]?.id ?? '');
  const [metric, setMetric] = useState<ChallengeMetric>('workouts');
  const [startDate, setStartDate] = useState(start0);
  const [endDate, setEndDate] = useState(suggestedEndDate('monthly', start0));
  const [target, setTarget] = useState('12');
  const [checkpoints, setCheckpoints] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const targetNumber = Number(target);
  const errors = {
    title: title.trim().length < 3 ? 'Informe um título.' : null,
    dates: endDate < startDate ? 'O término precisa ser igual ou posterior ao início.' : null,
    target: !(targetNumber > 0) ? 'Informe uma meta maior que zero.' : null
  };
  const valid = !errors.title && !errors.dates && !errors.target;
  const changeDuration = (next: ChallengeDuration) => {
    setDuration(next);
    setEndDate(suggestedEndDate(next, startDate));
    if (next !== 'annual') setCheckpoints(false);
  };
  const changeStart = (next: string) => {
    setStartDate(next);
    setEndDate(suggestedEndDate(duration, next));
  };
  const create = () => {
    setSubmitted(true);
    if (!valid) return;
    const challenge: CommunityChallenge = {
      id: `ch-new-${Date.now()}`,
      communityId,
      title: {
        'pt-BR': title.trim(),
        'en-US': title.trim()
      },
      description: {
        'pt-BR': '',
        'en-US': ''
      },
      duration,
      startDate,
      endDate,
      metric,
      target: targetNumber,
      progress: 0,
      participants: 0,
      ...(duration === 'annual' && checkpoints ? {
        checkpoints: Array.from({
          length: 12
        }, (_, month) => ({
          month,
          target: Math.ceil(targetNumber / 12),
          progress: 0
        }))
      } : {})
    };
    setRows(current => [challenge, ...current]);
    recordAudit({
      action: {
        'pt-BR': 'Criou desafio',
        'en-US': 'Created challenge'
      },
      entity: `challenge:${challenge.id}`,
      context: {
        'pt-BR': `${title.trim()} · ${t(`eng.ch.duration.${duration}`)}`,
        'en-US': `${title.trim()} · ${duration}`
      },
      after: `${brDate(startDate)} – ${brDate(endDate)} · meta ${targetNumber} ${t(`eng.ch.unit.${metric}`)}`,
      severity: 'info'
    });
    setCreated(title.trim());
    setCreating(false);
    setSubmitted(false);
    setTitle('');
  };
  const columns: Column<CommunityChallenge>[] = [{
    id: 'title',
    header: 'Desafio',
    width: '26%',
    sortValue: row => tl(row.title),
    cell: row => <span className="text-[13px] font-medium text-deep" data-id="element-429" data-mp="fm7s75mms1ly">{tl(row.title)}</span>
  }, {
    id: 'duration',
    header: 'Duração',
    sortValue: row => durations.indexOf(row.duration),
    cell: row => <span className="text-[12.5px] text-deep" data-id="element-430" data-mp="i6noh7jribbd">{t(`eng.ch.duration.${row.duration}`)}</span>
  }, {
    id: 'period',
    header: 'Período',
    sortValue: row => row.startDate,
    cell: row => <span className="bee-num whitespace-nowrap text-[12.5px] text-muted" data-id="element-431" data-mp="99dahefk882r">
          {brDate(row.startDate)} – {brDate(row.endDate)}
        </span>
  }, {
    id: 'community',
    header: 'Comunidade',
    cell: row => {
      const community = communities.find(item => item.id === row.communityId);
      return <span className="text-[12.5px] text-muted" data-id="element-432" data-mp="s6och6sktflw">{community ? tl(community.name) : row.communityId}</span>;
    }
  }, {
    id: 'target',
    header: 'Meta',
    align: 'right',
    sortValue: row => row.target,
    cell: row => <span className="bee-num whitespace-nowrap text-[12.5px] font-bold text-deep" data-id="element-433" data-mp="1jv7wgz7cbphc">
          {row.target.toLocaleString('pt-BR')} {t(`eng.ch.unit.${row.metric}`)}
        </span>
  }, {
    id: 'participants',
    header: 'Participantes',
    align: 'right',
    sortValue: row => row.participants,
    cell: row => <span className="bee-num text-[12.5px] text-deep" data-id="element-434" data-mp="1pijx12b3gwh9">{row.participants.toLocaleString('pt-BR')}</span>
  }, {
    id: 'status',
    header: 'Status',
    cell: row => {
      const status = challengeTimeline(row).status;
      return <StatusBadge status={statusTone[status]} label={statusLabel[status]} data-id="element-435" data-mp="1fn01wi4cu1tk" />;
    }
  }];
  const visible = filter === 'all' ? rows : rows.filter(row => row.duration === filter);
  const field = 'mt-1 h-10 w-full rounded-card border border-sand-line bg-white px-2 text-[13px] text-deep';
  return <div className="space-y-3" data-id="element-436" data-mp="11rxnt7pct65o">
      <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-437" data-mp="wuxhcvole1e1">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filtrar por duração" data-id="element-438" data-mp="rvktc7lzio9v">
          {(['all', ...durations] as const).map(id => <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)} className={`h-9 whitespace-nowrap rounded-[8px] px-2 text-[12px] font-medium transition-colors duration-150 ease-brand ${filter === id ? 'bg-deep text-sand' : 'border border-sand-line bg-white text-muted hover:text-deep'}`} data-id="element-439" data-mp="hr4ftkvz9j98">
              {id === 'all' ? 'Todos' : t(`eng.ch.duration.${id}`)}
            </button>)}
        </div>
        <Button variant={creating ? 'outline' : 'primary'} onClick={() => setCreating(open => !open)} data-id="element-440" data-mp="8zlyh6naoxek">
          {creating ? 'Cancelar' : 'Novo desafio'}
        </Button>
      </div>

      {created && <p role="status" className="rounded-card border border-sand-line bg-white p-2 text-[12.5px] text-deep" data-id="element-441" data-mp="3cgkr454sxu4n">
          Desafio “{created}” criado e registrado na auditoria. No protótipo ele existe até recarregar a página.
        </p>}

      {creating && <Panel title="Novo desafio" hint="Um único modelo para semanal, mensal e 12 meses." data-id="element-442" data-mp="zi1b92h8k1co">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3" data-id="element-443" data-mp="vgzbmkjepna6">
            <label className="block lg:col-span-3" data-id="element-444" data-mp="19f77ep4piijr">
              <span className="text-[12px] font-medium text-deep" data-id="element-445" data-mp="14fkr26tl0m6q">Título</span>
              <input value={title} onChange={event => setTitle(event.target.value)} aria-invalid={submitted && !!errors.title} aria-describedby="ch-title-error" className={field} data-id="element-446" data-mp="14hnr7jog7vp6" />
              {submitted && errors.title && <span id="ch-title-error" role="alert" className="mt-1 block text-[11.5px] text-deep" data-id="element-447" data-mp="1n62d5sjwsjj4">
                  {errors.title}
                </span>}
            </label>
            <label className="block" data-id="element-448" data-mp="1x48g8dhgbvpk">
              <span className="text-[12px] font-medium text-deep" data-id="element-449" data-mp="1x59s2k6akm8c">Duração</span>
              <select value={duration} onChange={event => changeDuration(event.target.value as ChallengeDuration)} className={field} data-id="element-450" data-mp="2gpihwkain2n1">
                {durations.map(id => <option key={id} value={id} data-id="element-451" data-mp="26ruivi5vn8z2">
                    {t(`eng.ch.duration.${id}`)}
                  </option>)}
              </select>
            </label>
            <label className="block" data-id="element-452" data-mp="2bsi6ruyt79p2">
              <span className="text-[12px] font-medium text-deep" data-id="element-453" data-mp="1x1j7jajv5xi5">Comunidade / categoria</span>
              <select value={communityId} onChange={event => setCommunityId(event.target.value)} className={field} data-id="element-454" data-mp="21srcnue5uwpi">
                {communities.map(community => <option key={community.id} value={community.id} data-id="element-455" data-mp="1t07dmstyiq45">
                    {tl(community.name)}
                  </option>)}
              </select>
            </label>
            <label className="block" data-id="element-456" data-mp="1j208r8cxzyii">
              <span className="text-[12px] font-medium text-deep" data-id="element-457" data-mp="1j0ykg26x1fiu">Tipo de desafio</span>
              <select value={metric} onChange={event => setMetric(event.target.value as ChallengeMetric)} className={field} data-id="element-458" data-mp="38dfcjb5mp95p">
                {metrics.map(id => <option key={id} value={id} data-id="element-459" data-mp="2yfiqvpf2evtc">
                    {metricLabel[id]}
                  </option>)}
              </select>
            </label>
            <label className="block" data-id="element-460" data-mp="33eubxamgsngq">
              <span className="text-[12px] font-medium text-deep" data-id="element-461" data-mp="3r4g9xur3own5">Início</span>
              <input type="date" value={startDate} onChange={event => changeStart(event.target.value)} className={field} data-id="element-462" data-mp="3r6ixm84s6dop" />
            </label>
            <label className="block" data-id="element-463" data-mp="3hf520bfy9y0u">
              <span className="text-[12px] font-medium text-deep" data-id="element-464" data-mp="3he3gte57zv5e">Término</span>
              <input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} aria-invalid={!!errors.dates} aria-describedby="ch-dates-error" className={field} data-id="element-465" data-mp="3mc56ihx5ispu" />
              {errors.dates && <span id="ch-dates-error" role="alert" className="mt-1 block text-[11.5px] text-deep" data-id="element-466" data-mp="23cffqptj63ba">
                  {errors.dates}
                </span>}
            </label>
            <label className="block" data-id="element-467" data-mp="2d6o4r3jcre4p">
              <span className="text-[12px] font-medium text-deep" data-id="element-468" data-mp="284yuhq3yf4ho">Meta</span>
              <span className="mt-1 flex items-center gap-2" data-id="element-469" data-mp="1kfwesnsvrlpt">
                <input type="number" min={1} value={target} onChange={event => setTarget(event.target.value)} aria-invalid={submitted && !!errors.target} className={`${field} mt-0`} data-id="element-470" data-mp="1kgfuryyz0017" />
                <span className="whitespace-nowrap text-[12px] text-muted" data-id="element-471" data-mp="1pdpj1bami5ng">{t(`eng.ch.unit.${metric}`)}</span>
              </span>
              {submitted && errors.target && <span role="alert" className="mt-1 block text-[11.5px] text-deep" data-id="element-472" data-mp="30te6e3rsh6os">
                  {errors.target}
                </span>}
            </label>
          </div>
          {duration === 'annual' && <label className="mt-3 flex items-start gap-2 text-[12.5px] text-deep" data-id="element-473" data-mp="22kfxbb5zbvoe">
              <input type="checkbox" checked={checkpoints} onChange={event => setCheckpoints(event.target.checked)} className="mt-[3px] h-4 w-4 accent-[#C89432]" data-id="element-474" data-mp="27i7o21uw1br0" />
              <span data-id="element-475" data-mp="1jrlq53ell7uq">
                Marcos mensais opcionais
                <span className="block text-[11.5px] text-muted" data-id="element-476" data-mp="1jpr7jvqstrtt">
                  Ficar abaixo de um marco não elimina o participante, a menos que o desafio defina essa regra.
                </span>
              </span>
            </label>}
          <p className="mt-3 text-[11.5px] text-muted" data-id="element-477" data-mp="w32jesb7534r">
            O término é sugerido pela duração e pode ser ajustado. Progresso dos participantes é privado por padrão.
          </p>
          <Button variant="primary" className="mt-3" onClick={create} data-id="element-478" data-mp="skvdjs3n42nh">
            Criar desafio
          </Button>
        </Panel>}

      <DataTable rows={visible} columns={columns} rowKey={row => row.id} pageSize={10} data-id="element-479" data-mp="nls6y380hode" />
    </div>;
}